import { clientEnv } from '@nwwp/env/client'
import { AnalyticsBrowser } from '@nwwp/segment-analytics'
import { type ABSmartlyEventLogger } from '../types'
import { logABSmartlyBucketingDecision } from './logBucketingDecision'
import { isStaticExperiment } from './server'
import type { SupportedExperimentVariants } from './types'
import { getVariantsArray } from './variants'

export type ExposureData = {
  experimentKey: string
  variationKey: string
  unitType: string | null
}

type Props = {
  analytics: AnalyticsBrowser
  staticExperimentResult: SupportedExperimentVariants
}

export function retrieveExternalDomainURL(url: string) {
  const oldUrl = new URL(url)
  let host = 'https://localnerd.stage.nerdwallet.biz'

  if (clientEnv.NEXT_PUBLIC_DEPLOYABLE_ENV === 'production') {
    host = 'https://www.nerdwallet.com'
  } else if (clientEnv.NEXT_PUBLIC_DEPLOYABLE_ENV === 'stage') {
    host = 'https://stage.nerdwallet.biz'
  }

  return new URL(oldUrl.toString().replace(`${oldUrl.protocol}//${oldUrl.host}`, host))
}

export const getABSmartlyEventLogger = ({ analytics, staticExperimentResult }: Props) => {
  const absmartlyEventLogger: ABSmartlyEventLogger = (context, eventName, data = {}) => {
    if (eventName === 'error') {
      let errorData = data

      if (!(errorData instanceof Error)) {
        errorData = new Error(`Unknown ABSmartly Error ${JSON.stringify(errorData)}`)
      }
    }

    if (eventName === 'publish' && 'exposures' in data) {
      const exposures = data.exposures ?? []
      const exposureEvents = exposures.reduce<ExposureData[]>((acc, exposure) => {
        const { name: experimentId, variant: variantIndex } = exposure
        const defaultEmptyValue = ''
        const variants = getVariantsArray(context, experimentId, defaultEmptyValue)
        const isStatic = isStaticExperiment(experimentId)
        const variationKey = (isStatic ? staticExperimentResult : variants[variantIndex]) ?? defaultEmptyValue
        const shouldSkipExposure = variationKey === defaultEmptyValue || exposure.audienceMismatch

        if (shouldSkipExposure) {
          return acc
        }
        return [
          ...acc,
          {
            variationKey,
            experimentKey: experimentId,
            unitType: exposure.unit ?? null,
          },
        ]
      }, [])

      const href = window.location?.href

      // Log the exposure data to Segment
      void logABSmartlyBucketingDecision(
        {
          attributes: {
            page_url: href ? retrieveExternalDomainURL(href).toString() : '',
          },
          exposureData: exposureEvents,
        },
        analytics
      )
    }
  }

  return absmartlyEventLogger
}
